import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';

import { withRouter, Link } from 'react-router-dom';

import LeadClientReportNotes from './show/client_report_notes';
import LeadInfo from './show/info';
import EditContactLink from './show/edit_contact_link';
import InviteToViewMenu from './show/invite_to_view_menu';
import LeadVaultProgress from './show/lead_vault_progress';
import PVPActivity from './show/pvp_activity';
import RequestMoreInformationNotes from './show/request_more_information_notes';
import LoadingSpinner from 'components/ui/loading_spinner';
import { getProject } from '../../selectors';
import LeadReferralSourceSelect from './show/lead_referral_source_select';
import LeadStatusSelect from './show/lead_status_select';
import DocAccessLevelSelect from 'bundles/leads/index/table/cells/doc_access_level';
import DocumentProgress from 'bundles/leads/index/table/cells/document_progress/index';
import {
  DOC_ACCESS_LEVEL_FOR_LEASE,
  DOC_ACCESS_LEVEL_FOR_SALE
} from 'bundles/leads/index/table/cell';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { fetchLeads, removeLead, showUpdateLeadModal } from 'bundles/project/actions/index';
import { fetchLead, refreshLead } from '../../actions/leads';
import { Card, Col, Row } from 'react-bootstrap-5';
import { Button } from 'components/ui/button';

export function LeadShow({
  lead,
  leadId,
  project,
  isFetchingLeads,
  features,
  fetchLead,
  fetchLeads,
  removeLead,
  refreshLead,
  showUpdateLeadModal,
  useVaultGates
}) {
  useEffect(() => {
    if (!lead && isFetchingLeads) {
      // this only occurs when navigating directly to a lead by URL
      // leads are fetched on mount in bundles/project/components/leads/list, which hasnt mounted yet
      fetchLeads(project.id);
    }
  }, [lead, isFetchingLeads]);

  // The leads list holds only its first page, so an older lead has to be fetched on its own.
  const [failedLeadId, setFailedLeadId] = useState(null);
  const lookupFailed = Object.is(failedLeadId, leadId);

  useEffect(() => {
    if (lead || isFetchingLeads || lookupFailed) return undefined;

    let current = true;
    fetchLead(project.id, leadId, () => current).catch(() => {
      if (current) setFailedLeadId(leadId);
    });

    return () => {
      current = false;
    };
  }, [lead, isFetchingLeads, leadId, lookupFailed]);

  if (isFetchingLeads || (!lead && !lookupFailed)) return <LoadingSpinner addBottomBuffer/>;

  if (!lead) {
    return (
      <div className="mt-5">
        Sorry, we couldn't find this lead.
      </div>
    );
  }

  const renderEditableFields = () => {
    return (
      <div className="form-vertical-field-container">
        <div className="form-group" key="lead-pi-status" name="lead-pi-status">
          <label className="form-label">Lead Status</label>
          <LeadStatusSelect lead={lead} />
        </div>
        <div className="form-group" key="lead-pi-referral-source" name="lead-pi-referral-source">
          <label className="form-label">Referral Source</label>
          <LeadReferralSourceSelect lead={lead} />
        </div>
        {project.leaseChecked && lead.listingLabel &&
          <div className="form-group" key="lead-pi-space" name="lead-pi-space">
            <label className="form-label">Space(s)</label>
            <p>{lead.listingLabel}</p>
          </div>
        }
        {renderDocsAccessLevelDropdowns()}
      </div>
    );
  };

  const renderDocsAccessLevelDropdowns = () => {
    const SelectOption = project.useVaultGates ? DocumentProgress : DocAccessLevelSelect;

    const dropdownForSale = (project.saleChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-sale" name="lead-pi-doc-access-level-for-sale">
        <label className="form-label">Doc Access Level for Sale</label>
        <SelectOption columnName={DOC_ACCESS_LEVEL_FOR_SALE} lead={lead} />
      </div>
    );

    const dropdownForLease = (project.leaseChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-lease" name="lead-pi-doc-access-level-for-lease">
        <label className="form-label">Doc Access Level for Lease</label>
        <SelectOption columnName={DOC_ACCESS_LEVEL_FOR_LEASE} lead={lead} />
      </div>
    );

    return (
      <React.Fragment>
        {dropdownForSale}
        {dropdownForLease}
      </React.Fragment>
    );
  };

  const newLeadDetails = () => {
    return (
      <div className="space-above">
        <LeadVaultProgress lead={lead} />
        {renderEditableFields()}
        {features.clientReport && <LeadClientReportNotes className="mt-4" lead={lead} />}
        <RequestMoreInformationNotes lead={lead} />
        <PVPActivity lead={lead} />
        <div className="mt-5">
          <Link name="delete-lead" to="/project_leads">
            <Button
              icon={faTrash}
              label="Delete Lead"
              variant="destructive"
              onClick={() => removeLead(project.id, lead)}
            />
          </Link>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="lead-show-page">
        <div className="mb-2">
          <Link name="lead-show-back-link" to="/project_leads">
            <FontAwesomeIcon className="me-2" icon={faChevronLeft}/>
            Back to Leads
          </Link>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1"><h5>{lead.displayName}</h5></div>
          <div>
            <InviteToViewMenu
              features={features}
              lead={lead}
              project={project}
              refreshLead={refreshLead}
              showUpdateLeadModal={showUpdateLeadModal}
              useVaultGates={useVaultGates}
            />
          </div>
          <div>
            <EditContactLink lead={lead}/>
          </div>
        </div>
        <Card className="mt-4">
          <Row className="align-items-stretch">
            <Col className="border-end" lg={3} md={4}>
              <Card.Body>
                <LeadInfo lead={lead}/>
                <EditContactLink lead={lead}/>
              </Card.Body>
            </Col>
            <Col>
              <Card.Body>
                {newLeadDetails()}
              </Card.Body>
            </Col>
          </Row>
        </Card>
      </div>
    </React.Fragment>
  );
}

LeadShow.propTypes = {
  features: PropTypes.shape({
    clientReport: PropTypes.bool.isRequired,
    leadInviteActivity: PropTypes.bool,
    projectTasksActivities: PropTypes.bool.isRequired
  }),
  fetchLead: PropTypes.func.isRequired,
  fetchLeads: PropTypes.func.isRequired,
  isFetchingLeads: PropTypes.bool,
  lead: PropTypes.object,
  leadId: PropTypes.number.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  project: PropTypes.object.isRequired,
  refreshLead: PropTypes.func.isRequired,
  removeLead: PropTypes.func.isRequired,
  showUpdateLeadModal: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired
};

const actions = {
  removeLead,
  refreshLead,
  fetchLead,
  fetchLeads,
  showUpdateLeadModal
};

export const mapStateToProps = (state, ownProps) => {
  const leadId = parseInt(ownProps.match.params['id']);

  // state.leads is an object (`{}` or `{ fetching: true }`) until a fetch lands, then an array of the loaded page,
  // which can be empty.
  const isFetchingLeads = !Array.isArray(state.leads);
  const lead = isFetchingLeads ? null : state.leads.find(lead => lead.id === leadId);

  return {
    features: state.features,
    isFetchingLeads: isFetchingLeads,
    lead: lead,
    leadId,
    project: getProject(state),
    useVaultGates: state.project.useVaultGates
  };
};

export default withRouter(connect(mapStateToProps, actions)(LeadShow));
